import { useState } from 'react';

import { api, ApiError } from '../../api';
import { go, goReplace } from '../../App';
import { usePolled } from '../../data';
import { ConnectionState, Loading, PageHead, Panel, Stars, formatWhen } from '../../ui';
import { ReviewAggregate } from '../../types';

// H4 — the supplier directory and the invite form. The directory is the
// hotel's working list: active suppliers, requested connections awaiting the
// supplier's approval, and invitations still in flight — including
// `declined to join` and `this reached the wrong person`, which are stories
// the hotel may need to act on, not rows to hide.

interface DirectoryItem {
  kind: 'connection' | 'invitation';
  supplierAccountId?: string;
  inviteId?: string;
  name: string;
  email?: string;
  phone?: string;
  status: 'active' | 'requested' | 'invited' | 'declined' | 'burned';
  invitedAt?: string;
  lastSentAt?: string;
  expired?: boolean;
  // The supplier's review aggregate (DECISION-056) — present on connection rows,
  // read from the supplier's own profile, never their bookings (DECISION-020).
  rating?: ReviewAggregate;
}

const FIVE_DAYS_MS = 5 * 86_400_000;

// Label plus the connection-state kind the shared <ConnectionState> chip draws
// (canon 04 "Connection state") — the hotel's wording; the colour is the widget's.
const STATUS_COPY: Record<DirectoryItem['status'], { label: string; kind: 'connected' | 'pending' | 'dormant' }> = {
  active: { label: 'Active', kind: 'connected' },
  requested: { label: 'Connection requested', kind: 'pending' },
  invited: { label: 'Invited', kind: 'pending' },
  declined: { label: 'Invited · declined to join', kind: 'dormant' },
  burned: { label: 'Invitation reached the wrong person', kind: 'dormant' },
};

export function Suppliers({ invite }: { invite?: boolean }) {
  const { data, error, reload } = usePolled<{ items: DirectoryItem[] }>('/suppliers');
  const items = data ? data.items : null;

  if (invite) return <InviteForm onDone={() => { reload(); goReplace('/suppliers'); }} />;

  return (
    <div>
      <PageHead title="Suppliers" sub="Who you can book, and who you have asked.">
        <a href="#/suppliers/invite" className="btn" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
          Invite supplier
        </a>
      </PageHead>

      {error && items === null && <p className="error-line">{error}</p>}
      {items === null ? (
        <Loading />
      ) : items.length === 0 ? (
        <div className="empty-neutral">No suppliers yet — invite the ones you already work with.</div>
      ) : (
        <Panel flush>
          <table>
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Status</th>
                <th>Since</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.supplierAccountId ?? s.inviteId}>
                  <td>
                    {s.supplierAccountId ? (
                      <a href={`#/suppliers/${s.supplierAccountId}`} style={{ fontWeight: 600 }}>
                        {s.name}
                      </a>
                    ) : (
                      s.name
                    )}
                    {s.email && (
                      <>
                        <br />
                        <span className="hint">{s.email}</span>
                      </>
                    )}
                    {/* The supplier's rating (DECISION-056) — shown once they've
                        been reviewed, so the hotel sees who is dependable. */}
                    {s.rating && s.rating.count > 0 && (
                      <>
                        <br />
                        <span className="hint">
                          <Stars rating={s.rating.average} /> {s.rating.average.toFixed(1)} ({s.rating.count})
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    <ConnectionState
                      kind={STATUS_COPY[s.status].kind}
                      label={s.status === 'invited' && s.expired ? 'Invited · link expired' : STATUS_COPY[s.status].label}
                    />
                    {/* No response in 5 days → the hotel is prompted to
                        call, the number one tap away (H4's edge). */}
                    {s.status === 'invited' && !s.expired && s.lastSentAt &&
                      Date.now() - Date.parse(s.lastSentAt) > FIVE_DAYS_MS && s.phone && (
                        <span className="hint" style={{ display: 'block', marginTop: 4 }}>
                          No response — <a href={`tel:${s.phone}`}>call {s.phone}</a>
                        </span>
                      )}
                  </td>
                  <td className="mono">{s.invitedAt ? formatWhen(s.invitedAt).split('·')[0] : '—'}</td>
                  <td>
                    {s.kind === 'invitation' && s.status !== 'burned' && (
                      <ResendButton item={s} onSent={reload} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

// "Expired → re-issued by the hotel in one click, never by support"
// (canon 11's edge): the stored three fields go straight back to the API.
function ResendButton({ item, onSent }: { item: DirectoryItem; onSent: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api('POST', '/suppliers/invitations', {
        businessName: item.name,
        email: item.email,
        phone: item.phone,
      });
      await onSent();
    } finally {
      setBusy(false);
    }
  };
  return (
    <button className="btn secondary" disabled={busy || !item.phone || !item.email} onClick={send}>
      {busy ? 'Sending…' : 'Re-send'}
    </button>
  );
}

// Three fields, and the three are the whole gate (H4). An invalid number is
// caught before sending — by the API, whose word is final.
function InviteForm({ onDone }: { onDone: () => void }) {
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState<'invited' | 'connection-request' | 'already-connected' | null>(null);

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api<{ kind: 'invited' | 'connection-request' | 'already-connected' }>(
        'POST',
        '/suppliers/invitations',
        { businessName, email, phone },
      );
      setSent(res.kind);
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div>
        <PageHead title="Invitation sent" sub="" />
        <Panel>
          <p style={{ marginBottom: 12 }}>
            {sent === 'invited'
              ? `${businessName} has the invitation — the link works for 14 days. You'll see them turn Active here once they've signed in and their trips are set up.`
              : sent === 'connection-request'
                ? `${businessName} is already on ISO — they've been asked to connect with you instead. No second account, nothing to re-type.`
                : `${businessName} is already connected — you can book them now.`}
          </p>
          <button className="btn" onClick={onDone}>Back to suppliers</button>
        </Panel>
      </div>
    );
  }

  return (
    <div>
      <PageHead title="Invite a supplier" sub="Three fields — they sign in on their phone, and we set up what they offer with them." />
      <Panel>
        <div className="field">
          <label htmlFor="bn">Business name</label>
          <input id="bn" value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="em">Contact email</label>
          <input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="ph">Mobile phone</label>
          <input id="ph" className="mono" placeholder="+30 691 234 5678" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <p className="hint" style={{ marginTop: 6 }}>
            The phone is their sign-in — if this number is already on ISO, this becomes a connection request instead of a new account.
          </p>
        </div>
        {error && <p className="error-line" role="alert">{error}</p>}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="btn secondary" onClick={() => go('/suppliers')}>Cancel</button>
          <button className="btn" disabled={busy || !businessName.trim() || !email.trim() || !phone.trim()} onClick={send}>
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </Panel>
    </div>
  );
}
